import {AutoInitializing, UnLitElement, WithEopSlots} from "../../common/elements";
import {customElement, property, query, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {ManagingResources} from "../../common/lifetime";
import {Dictionary} from "./dictionary";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";
import {classMap} from "lit/directives/class-map.js";
import {ScrollableBar} from "./scrollableBar";

export type TabConfiguration = {
    title: string;
    slotName: string;
};

@customElement("eop-tabs")
export class EopTabs extends WithEopSlots(ManagingResources(AutoInitializing(UnLitElement))) {

    @property({attribute: "tab-layout", type: String})
    public layout: string = "horizontal";

    @property({attribute: "tab-configurations", type: Array})
    private tabConfigurations: TabConfiguration[] = [];

    @query(".tab-navigation")
    private tabNavigation: HTMLElement;

    @state()
    private activeTabIndex: number = 0;

    private scrollBar: ScrollableBar;
    protected dictionary: Dictionary;

    public override connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    public override disconnectedCallback(): void {
        this.scrollBar.disconnect();
        super.disconnectedCallback();
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.scrollBar = new ScrollableBar(this.tabNavigation, this.dictionary);
    }

    public render(): TemplateResult {
        return html`
            <div class="tab-container ${this.layout}">
                <div class="tab-navigation" aria-label=${this.dictionary.translate("MSG_TAB_NAVIGATION")}>
                    <div role="tablist" class="tabs horizontally-scrollable-area">
                        ${(this.renderTabLabels())}
                    </div>
                </div>
                ${(this.renderTabContents())}
            </div>
        `;
    }

    private renderTabContents(): TemplateResult[] {
        return this.tabConfigurations.map((tab, index) => {
            return html`
                <div class=${classMap({
                    "tab-contents": true,
                    "active": this.isTabActive(index),
                    "layout-element-nested-8": this.layout === "vertical"
                })}>
                    ${this.resolveSlots(tab.slotName)}
                </div>`;
        });
    }

    private renderTabLabels(): TemplateResult[] {
        return this.tabConfigurations.map((tab, index) => {
            const handleTabClick = (e: Event): void => this.clickTab(e, index);
            return html`
                <button
                        role="tab"
                        class="tab scrollable-item ${this.isTabActive(index) ? "active" : ""}"
                        aria-selected=${this.isTabActive(index)}
                        @click=${handleTabClick}
                        ${trackingButtonInteract(tab.title)}>
                    <span class="tab-label audible">${tab.title}</span>
                </button>`;
        });
    };

    private clickTab(event: Event, index: number): void {
        event.preventDefault();
        this.activeTabIndex = index;
        this.scrollBar.scrollToItem(index);
    }

    private isTabActive(index: number): boolean {
        return this.activeTabIndex === index;
    }
}
